import { type ReactNode } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import {
  AreaFilter,
  CollapsibleFilterToolbar,
  FilterGrid,
  type FilterGridItem,
  type ToolbarStatusItem,
} from '~/components/common/filters'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import { ExpiredFilterToggle } from '~/pages/AdminOcctoMaster/tabs/_common/components/ExpiredFilterToggle'
import { SearchInput } from '~/pages/AdminOcctoMaster/tabs/_common/components/SearchInput'
import { useGetPersonalSettingsQuery } from '~/pages/Settings/hooks/useGetPersonalSettingsQuery'

/**
 * 広域機関マスタの開閉の保存キー (CollapsibleFilterToolbar が接頭辞を足す)。
 * 開閉は画面で 1 つ (タブを替えても開いたまま・閉じたまま)。 事業者タブ (エリアなし) も同じキーを使う。
 */
export const OCCTO_MASTER_FILTER_STORAGE_KEY = 'adminOcctoMaster'

/**
 * タブ固有の条件 (計画区分など)。 エリアの右・検索の左のマスに並べ、 閉じた時の要約にも値を出す。
 * 「扱う対象を選ぶ」項目 (発電販売計画の計画区分) は件数に数えず、 クリアでも戻さない (React mock と同じ)。
 */
export type MasterTabFilter = {
  key: string
  label: string
  control: ReactNode
  /** 閉じた時の要約に出す今の値。 */
  summaryValue: string
  /**
   * 既定から変えた条件か。 渡した条件だけ件数に数え、 クリアで `onClear` を呼ぶ (絞り込みの条件)。
   * 渡さない条件 (発電販売計画の計画区分 = 扱う対象を選ぶ) は数えず戻さない。
   */
  isModified?: boolean
  onClear?: () => void
}

/** 失効表示トグルの状態 (終了した行を表に含めるか。 既定 = 含めない)。 */
export type MasterExpiredFilter = {
  subject: string
  expiredCount: number
  includeExpired: boolean
  onChange: (v: boolean) => void
}

type Props = {
  selectedAreaCode: AreaCode
  onSelectArea: (a: AreaCode) => void
  searchText: string
  onSearchTextChange: (s: string) => void
  /** 検索対象がタブごとに異なるため、 placeholder は呼び出し側から渡す。 */
  searchPlaceholder: string
  /**
   * エリアの右・検索の左に差し込むタブ固有の条件。
   * design/admin/occto.html の `.ecl-toolbar` は タブ固有のフィルタも
   * エリア → (ここ) → 検索 の順で並べるため、 別行に切り出さずここに差し込む
   * (現在の利用者は発電販売計画タブの計画区分のみ)。
   */
  afterArea?: MasterTabFilter
  /**
   * afterArea の右・検索の左に並べるタブ固有の条件 (電源ユニットマスタの「その他」など)。
   * 並びは エリア → タブ固有の条件 → 検索 (React mock の電源ユニットマスタと同じ)。
   */
  extraFilters?: MasterTabFilter[]
  /**
   * 失効表示トグル (検索の右)。 既定から変えた条件として数え、 クリアで「含めない」に戻す。
   * 件数はラベルに括弧で出す ([[ExpiredFilterToggle]])。
   */
  expiredFilter?: MasterExpiredFilter
  /**
   * 開閉の状態を残す localStorage のキー。 既定は広域機関マスタ。 別の画面 (電源ユニットマスタ) が
   * この部品を使う時は画面のキーを渡す (開閉は画面ごとに残す)。
   */
  storageKey?: string
}

/**
 * 広域マスタ非事業者タブ共通の開閉式フィルター「エリア + 検索」 (spec `occto-master/index.md`
 * AC-002。 UI 辞書 toolbar-collapsible.md、 React mock `AdminOcctoMaster` の `commonFilters`)。
 * 業務エリア設定 (個人設定) に含まれるエリアのみを選択肢に絞り込む
 * (「すべて」は常に表示)。 個人設定が未設定・取得中・取得エラーの場合は全エリアに
 * fallback する (`~/pages/SupplyGap/index.tsx` の既存前例に合わせる)。
 *
 * - 要約 = エリア → 計画区分(あれば) → 検索(入れている時だけ) → 失効表示
 * - 件数・クリア = エリア・検索・失効表示 (計画区分は扱う対象なので数えず戻さない)
 */
export const MasterFilterToolbar = ({
  selectedAreaCode,
  onSelectArea,
  searchText,
  onSearchTextChange,
  searchPlaceholder,
  afterArea,
  extraFilters = [],
  expiredFilter,
  storageKey = OCCTO_MASTER_FILTER_STORAGE_KEY,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { data } = useGetPersonalSettingsQuery()
  const businessAreas = data?.settings?.businessAreas ?? []
  const areaOptions =
    businessAreas.length > 0
      ? AREA_CODE_OPTIONS.filter((code) => businessAreas.includes(code))
      : AREA_CODE_OPTIONS

  const areaLabel = t('page:adminOcctoMaster.toolbar.area')
  const searchLabel = t('page:adminOcctoMaster.toolbar.searchLabel')
  const isAreaModified = selectedAreaCode !== AreaCode.UNSPECIFIED
  const isSearchModified = searchText !== ''
  const isExpiredModified = expiredFilter?.includeExpired === true
  const tabFilters = [
    ...(afterArea === undefined ? [] : [afterArea]),
    ...extraFilters,
  ]

  const items: FilterGridItem[] = [
    {
      key: 'area',
      label: areaLabel,
      // エリア選択。 design (`design/admin/occto.html` #occto-area-group) は
      // ラジオで「すべて」+ 10 エリアの 11 択、 既定「すべて」。 共通 AreaFilter
      // (radio) を使い、 AreaCode.UNSPECIFIED を「すべて」の sentinel として
      // undefined に橋渡しする。
      control: (
        <AreaFilter
          allLabel={t('page:adminOcctoMaster.toolbar.areaAll')}
          areaCodes={areaOptions}
          onChange={(area) => onSelectArea(area ?? AreaCode.UNSPECIFIED)}
          value={isAreaModified ? selectedAreaCode : undefined}
          variant="radio"
        />
      ),
    },
    ...(afterArea === undefined
      ? []
      : [
          {
            key: afterArea.key,
            label: afterArea.label,
            control: afterArea.control,
          },
        ]),
    ...extraFilters.map((filter) => ({
      key: filter.key,
      label: filter.label,
      control: filter.control,
    })),
    // 検索 input
    //
    // **検索テキストは AC-SC-031 (フィルタ選択状態の localStorage 保持) の対象に
    // 含めない。** `shared-components.md` 機能詳細 5 が「フィルタバーを持つ画面は
    // AC-SC-031 により保持を必須とする (画面個別の spec で改めて規定しない)」と
    // するため、 除外の判断はここに残す。
    //
    // - AC-SC-031 が復元を求めるのは「フィルタの**選択状態**」で、 同 §6 の
    //   AC-SC-019〜021 と機能詳細 2 はそれをボタングループの単一選択 / 複数選択と
    //   定義している。 自由入力テキストを扱う AC は §6 に無い
    // - 広域マスタは「タブ切り替え時にフィルタ条件をリセット」
    //   (`admin/master/occto-master/index.md` 機能詳細 1) を、 **選択中タブのみを
    //   マウントする構造**で満たしている (`AdminOcctoMaster/index.tsx` の
    //   `ActiveTabComponent`)。 mount 時に復元するとタブ往復で検索語が戻り、
    //   この規定を破る
    // - 検索で 0 件になった時、 この画面は `common:table.empty`「データがありません」
    //   しか出せない (`useMasterCrudTab` の `emptyLabel` は失効非表示で全滅した時
    //   のみ設定)。 復元語がヒットしないと**マスタが空だと誤認させる**
    //
    // 将来保持するなら (1) タブを次元に含む保存キー (2) 検索起因の 0 件を区別する
    // 空状態、 の 2 点を同時に入れること。 なお同じ理由でエリア / 失効トグルも
    // 素直には保持できない (この画面全体の AC-SC-031 対応方針は issue 参照)。
    {
      key: 'search',
      label: searchLabel,
      control: (
        <SearchInput
          onChange={onSearchTextChange}
          placeholder={searchPlaceholder}
          value={searchText}
        />
      ),
    },
    ...(expiredFilter === undefined
      ? []
      : [
          {
            key: 'expired',
            // ラベルは checkbox の文言そのもの (単独の checkbox は条件で包む)
            control: (
              <ExpiredFilterToggle
                expiredCount={expiredFilter.expiredCount}
                includeExpired={expiredFilter.includeExpired}
                onChange={expiredFilter.onChange}
                subject={expiredFilter.subject}
              />
            ),
          },
        ]),
  ]

  const summary: ToolbarStatusItem[] = [
    {
      key: 'area',
      label: areaLabel,
      value: isAreaModified
        ? convertAreaLabel(selectedAreaCode)
        : t('page:adminOcctoMaster.toolbar.areaAll'),
    },
    ...(afterArea === undefined
      ? []
      : [
          {
            key: afterArea.key,
            label: afterArea.label,
            value: afterArea.summaryValue,
          },
        ]),
    // 値が空の条件 (何も選んでいない「その他」など) は要約に出さない (検索と同じ)
    ...extraFilters
      .filter((filter) => filter.summaryValue !== '')
      .map((filter) => ({
        key: filter.key,
        label: filter.label,
        value: filter.summaryValue,
      })),
    // 検索は入れている時だけ (React mock と同じ。 エリアが先頭にあるので要約が空にならない)
    ...(isSearchModified
      ? [{ key: 'search', label: searchLabel, value: searchText }]
      : []),
    ...(expiredFilter === undefined
      ? []
      : [
          {
            key: 'expired',
            label: t('page:adminOcctoMaster.common.includeExpired', {
              subject: expiredFilter.subject,
            }),
            value: expiredFilter.includeExpired
              ? t('page:adminOcctoMaster.common.valueYes')
              : t('page:adminOcctoMaster.common.valueNo'),
          },
        ]),
  ]

  return (
    <CollapsibleFilterToolbar
      modifiedCount={
        [
          isAreaModified,
          isSearchModified,
          isExpiredModified,
          ...tabFilters.map((filter) => filter.isModified === true),
        ].filter(Boolean).length
      }
      onClear={() => {
        onSelectArea(AreaCode.UNSPECIFIED)
        onSearchTextChange('')
        expiredFilter?.onChange(false)
        for (const filter of tabFilters) filter.onClear?.()
      }}
      storageKey={storageKey}
      summary={summary}
    >
      <FilterGrid items={items} />
    </CollapsibleFilterToolbar>
  )
}
